import { AnyAbility } from '@casl/ability'
import { Button, Typography } from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'
import { APIError, Establishment, SlateCategory } from '@sugg-gestion/ubidreams-react-suggpro'
import clsx from 'clsx'
import SubmitButton from 'components/common/submitButton'
import AddForm, { AddFormState } from 'components/forms/slate/addForm'
import AuthenticateActions from 'components/layouts/components/authenticateActions'
import AuthenticateContent from 'components/layouts/components/authenticateContent'
import React from 'react'
import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SocialNetwork } from '../../../containers/slate'
import DialogMultipleLoading, { LoadingState } from '../../dialogs/dialogMultipleLoading'
import { DefaultDeleteFlagFormState } from '../../forms/slate/defaultDeleteFlagForm'
import { useStyles } from './styles.css'

interface Props {
    extraSharing?: React.ReactNode
    establishment: Establishment
    onSubmit: (values: AddFormState) => void
    inProgress: boolean
    updateEstablishmentInProgress: boolean
    initialValues: AddFormState
    onBack: () => void
    categories?: Array<SlateCategory>
    isFacebookLinked: boolean
    isInstagramLinked: boolean
    isFacebookSignedExpired: boolean
    isGoogleLinked: boolean
    isGoogleSignedExpired: boolean
    isTwitterLinked: boolean
    isTwitterSignedExpired: boolean
    createInProgress: boolean
    shareOnFacebookInProgress: boolean
    shareOnInstagramInProgress: boolean
    shareOnTwitterInProgress: boolean
    shareOnGoogleInProgress: boolean
    ability: AnyAbility
    addError?: APIError
    facebookError?: APIError
    googleError?: APIError
    instagramError?: APIError
    twitterError?: APIError
    openLoadingDialog: boolean
    closeLoadingDialog: () => void
    loadingNetworks?: Array<SocialNetwork>
    onUpdateEstablishment: (
        values: DefaultDeleteFlagFormState,
    ) => Promise<{ shouldClose: boolean; shouldDelete: boolean }>
}

const AddSlateView: React.FC<Props> = ({
    establishment,
    onBack,
    inProgress,
    updateEstablishmentInProgress,
    createInProgress,
    shareOnFacebookInProgress,
    shareOnInstagramInProgress,
    shareOnGoogleInProgress,
    shareOnTwitterInProgress,
    ability,
    addError,
    facebookError,
    googleError,
    instagramError,
    twitterError,
    openLoadingDialog,
    closeLoadingDialog,
    loadingNetworks,
    onUpdateEstablishment,
    ...formData
}) => {
    const classes = useStyles()
    const establishmentBasePath = '/establishment/:id'.replace(':id', establishment.id.toString() ?? '')

    const loading = () => {
        const loadingStates: Array<LoadingState> = [
            {
                title: <Trans i18nKey={'views.slate.add.inProgress'} />,
                start: true,
                finish: !createInProgress,
                error: addError,
                hasError: addError !== undefined,
            },
        ]
        loadingNetworks?.forEach((network) => {
            switch (network) {
                case SocialNetwork.Facebook:
                    loadingStates.push({
                        title: <Trans i18nKey={'views.slate.add.shareOnFacebookInProgress'} />,
                        start: !createInProgress && shareOnFacebookInProgress,
                        finish: !createInProgress && !shareOnFacebookInProgress,
                        error: facebookError,
                        hasError: facebookError !== undefined || addError !== undefined,
                    })
                    break
                case SocialNetwork.Instagram:
                    loadingStates.push({
                        title: <Trans i18nKey={'views.slate.add.shareOnInstagramInProgress'} />,
                        start: !createInProgress && shareOnInstagramInProgress,
                        finish: !createInProgress && !shareOnInstagramInProgress,
                        error: instagramError,
                        hasError: instagramError !== undefined || addError !== undefined,
                    })
                    break
                case SocialNetwork.Google:
                    loadingStates.push({
                        title: <Trans i18nKey={'views.slate.add.shareOnGoogleInProgress'} />,
                        start: !createInProgress && shareOnGoogleInProgress,
                        finish: !createInProgress && !shareOnGoogleInProgress,
                        error: googleError,
                        hasError: googleError !== undefined || addError !== undefined,
                    })
                    break
                case SocialNetwork.Twitter:
                    loadingStates.push({
                        title: <Trans i18nKey={'views.slate.add.shareOnTwitterInProgress'} />,
                        start: !createInProgress && shareOnTwitterInProgress,
                        finish: !createInProgress && !shareOnTwitterInProgress,
                        error: twitterError,
                        hasError: twitterError !== undefined || addError !== undefined,
                    })
                    break
            }
        })
        return loadingStates
    }
    return (
        <AddForm
            canPublishMultiplePhoto={ability.can('api.multi_photo_access')}
            canAutomaticallyDelete={ability.can('api.auto_delete_access')}
            inProgress={inProgress}
            updateEstablishmentInProgress={updateEstablishmentInProgress}
            showInstagram
            showTwitter={false}
            {...formData}
            establishment={establishment}
            onUpdateEstablishment={onUpdateEstablishment}
        >
            {(content, actions) => (
                <>
                    <AuthenticateContent>
                        {openLoadingDialog && (
                            <DialogMultipleLoading onClose={closeLoadingDialog} loading={loading()} />
                        )}
                        <Typography variant="h1" color="textSecondary" className={classes.title}>
                            <Trans i18nKey="views.slate.title" />
                            <Button
                                className={classes.setting}
                                variant="outlined"
                                component={Link}
                                to={establishmentBasePath + '/slate/setting?withBack'}
                                startIcon={<SettingsIcon />}
                            >
                                <Trans i18nKey="actions.setting" />
                            </Button>
                        </Typography>
                        {content}
                    </AuthenticateContent>
                    <AuthenticateActions>
                        <SubmitButton variant="contained" onClick={onBack}>
                            <Trans i18nKey="actions.back" />
                        </SubmitButton>
                        {actions.map((action, index) => (
                            <SubmitButton
                                key={index}
                                variant="contained"
                                color="primary"
                                {...action}
                                className={clsx(action.className)}
                            />
                        ))}
                    </AuthenticateActions>
                </>
            )}
        </AddForm>
    )
}

export default AddSlateView
